import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Button, Popover, TextField } from '@radix-ui/themes';
import { useId, useState } from 'react';
import { Team } from '../api';

interface Props {
  teams: Team[];
  selectedTeam: number | null;
  onTeamChange: (teamId: number | null) => void;
  label: string;
  unavailable?: boolean;
}

export default function SearchableTeamSelect({ teams, selectedTeam, onTeamChange, label, unavailable }: Props) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const search = query.trim().toLowerCase();
  const matches = teams.filter(team => team.name.toLowerCase().includes(search));
  if (search) {
    matches.sort((a, b) => a.name.toLowerCase().indexOf(search) - b.name.toLowerCase().indexOf(search));
  }
  const options = search ? matches : [{ id: null, name: 'All teams' }, ...matches];
  const selectedName = teams.find(team => team.id === selectedTeam)?.name;

  function changeOpen(nextOpen: boolean) {
    setOpen(nextOpen);
    setQuery('');
    setActiveIndex(-1);
  }

  function select(teamId: number | null) {
    onTeamChange(teamId);
    changeOpen(false);
  }

  return (
    <div className="team-picker">
      <span id={`${id}-label`} className="field-label">{label}</span>
      <Popover.Root open={open} onOpenChange={changeOpen}>
        <Popover.Trigger>
          <Button type="button" variant="surface" color="gray" size="3" className="team-trigger"
            aria-labelledby={`${id}-label ${id}-value`} disabled={unavailable}>
            <span id={`${id}-value`}>{unavailable ? 'Teams unavailable' : selectedName ?? 'All teams'}</span>
            <ChevronDownIcon aria-hidden="true" />
          </Button>
        </Popover.Trigger>
        <Popover.Content className="team-popover" align="start" sideOffset={8} aria-label="Choose a team">
          <TextField.Root
            type="search"
            role="combobox"
            aria-label="Search teams"
            aria-autocomplete="list"
            aria-controls={`${id}-options`}
            aria-expanded={open}
            aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
            value={query}
            placeholder="Search teams…"
            onChange={event => {
              setQuery(event.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={event => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (options.length === 0) return;
                const nextIndex = event.key === 'ArrowDown'
                  ? (activeIndex + 1) % options.length
                  : (activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
                setActiveIndex(nextIndex);
                document.getElementById(`${id}-option-${nextIndex}`)?.scrollIntoView({ block: 'nearest' });
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                const option = options[Math.max(activeIndex, 0)];
                if (option) select(option.id);
              }
            }}
          >
            <TextField.Slot><MagnifyingGlassIcon /></TextField.Slot>
          </TextField.Root>
          <div className="team-options" id={`${id}-options`} role="listbox" aria-label="Teams">
            {options.map((option, index) => (
              <div
                key={option.id ?? 'all'}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={option.id === selectedTeam}
                data-active={index === activeIndex || undefined}
                onMouseDown={event => event.preventDefault()}
                onClick={() => select(option.id)}
              >
                {option.name}
                {option.id === selectedTeam && <CheckIcon aria-hidden="true" />}
              </div>
            ))}
          </div>
          {options.length === 0 && <div className="team-empty" role="status">No teams match your search.</div>}
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
